import * as React from "react";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import {
  treatmentPlanForPatient,
  type TreatmentStep,
} from "./clinical-snippets.ts";
import {
  RichTextEditor,
  RichTextDisplay,
} from "../shared/rich-text-editor.component.tsx";

function buildHtml(steps: TreatmentStep[]): string {
  return `
<h2>Plan de traitement</h2>
${steps
  .map(
    (s) => `
<h3>${s.phase}</h3>
<p>${s.description}</p>
<ul>
  <li><strong>Durée :</strong> ${s.duration}</li>
  <li><strong>Statut :</strong> ${s.status === "done" ? "Terminée" : s.status === "current" ? "En cours" : "À venir"}</li>
  ${s.startDate ? `<li><strong>Début :</strong> ${new Date(s.startDate).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</li>` : ""}
  ${s.endDate ? `<li><strong>Fin :</strong> ${new Date(s.endDate).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</li>` : ""}
</ul>
`,
  )
  .join("")}
`.trim();
}

export function TreatmentPlanWidget() {
  const { current } = Interactions.usePatient();
  const toast = Interactions.useToast();

  const editsRef = React.useRef<Map<string, string>>(new Map());
  const [editing, setEditing] = React.useState(false);
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  const seedHtml = React.useMemo(
    () => (current ? buildHtml(treatmentPlanForPatient(current.uuid)) : ""),
    [current?.uuid],
  );

  React.useEffect(() => {
    setEditing(false);
  }, [current?.uuid]);

  if (!current) {
    return (
      <div className="flex h-full min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-sf-border py-8 text-center">
        <I.ListChecks size={20} className="mb-2 text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  const html = editsRef.current.get(current.uuid) ?? seedHtml;

  const handleSave = (newHtml: string) => {
    editsRef.current.set(current.uuid, newHtml);
    setEditing(false);
    force();
    toast.push({ type: "success", title: "Plan de traitement enregistré" });
  };

  if (editing) {
    return (
      <RichTextEditor
        defaultHtml={html}
        placeholder="Saisir le plan de traitement…"
        onSave={handleSave}
        onCancel={() => setEditing(false)}
        className="h-full"
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sf-text-muted">
          <I.ListChecks size={12} />
          Plan de traitement
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-gray-50"
          title="Modifier"
        >
          <I.Edit size={13} />
        </button>
      </div>
      <div className="rounded-lg border border-sf-border bg-white p-2.5">
        <RichTextDisplay html={html} />
      </div>
    </div>
  );
}
